import { useState } from 'react';
import {
  IconSearch,
  IconPlus,
  IconEye,
  IconSend,
  IconDownload,
  IconDotsVertical,
} from '@tabler/icons-react';
import { ActionIcon, Badge, Box, Button, Card, Group, Select, Stack, Table, Text, TextInput, Title } from '@/components/ui';
import { mockInvoices, Invoice } from '../mockData/invoicing';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const getStatusColor = (status: Invoice['status']) => {
  switch (status) {
    case 'paid': return 'green';
    case 'sent': return 'blue';
    case 'viewed': return 'cyan';
    case 'overdue': return 'red';
    case 'draft': return 'gray';
    case 'cancelled': return 'orange';
    default: return 'gray';
  }
};

export default function InvoicesPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const statuses = ['draft', 'sent', 'viewed', 'paid', 'overdue', 'cancelled'];

  const filteredInvoices = mockInvoices.filter(invoice => {
    const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(search.toLowerCase()) ||
                          invoice.clientName.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Sort by issue date descending
  const sortedInvoices = [...filteredInvoices].sort((a, b) => b.issueDate.getTime() - a.issueDate.getTime());

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Invoices</Title>
          <Text c="dimmed" size="sm">{filteredInvoices.length} invoices</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>Create Invoice</Button>
      </Group>

      {/* Summary Cards */}
      <Group>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Total</Text>
          <Text size="xl" fw={700}>{mockInvoices.length}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Paid</Text>
          <Text size="xl" fw={700} c="green">{mockInvoices.filter(i => i.status === 'paid').length}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Pending</Text>
          <Text size="xl" fw={700} c="blue">{mockInvoices.filter(i => ['sent', 'viewed'].includes(i.status)).length}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Overdue</Text>
          <Text size="xl" fw={700} c="red">{mockInvoices.filter(i => i.status === 'overdue').length}</Text>
        </Card>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search invoices..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={150}
          />
        </Group>
      </Card>

      {/* Invoices Table */}
      <Card>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Invoice</Table.Th>
              <Table.Th>Client</Table.Th>
              <Table.Th>Issue Date</Table.Th>
              <Table.Th>Due Date</Table.Th>
              <Table.Th ta="right">Amount</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th w={100}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedInvoices.map(invoice => (
              <Table.Tr key={invoice.id}>
                <Table.Td>
                  <Text size="sm" fw={500}>{invoice.invoiceNumber}</Text>
                </Table.Td>
                <Table.Td>
                  <Box>
                    <Text size="sm">{invoice.clientName}</Text>
                    <Text size="xs" c="dimmed">{invoice.clientEmail}</Text>
                  </Box>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">{formatDate(invoice.issueDate)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c={invoice.status === 'overdue' ? 'red' : 'dimmed'}>
                    {formatDate(invoice.dueDate)}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm" fw={500}>{formatCurrency(invoice.total)}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" color={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon variant="light" size="sm" title="View">
                      <IconEye size={14} />
                    </ActionIcon>
                    {invoice.status === 'draft' && (
                      <ActionIcon variant="light" size="sm" title="Send">
                        <IconSend size={14} />
                      </ActionIcon>
                    )}
                    <ActionIcon variant="light" size="sm" title="Download">
                      <IconDownload size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
